/**
 * A store's public "become our affiliate" page, sellhiz.win/@<handle>/affiliate:
 * its address, the handle it answers to, and the invite text a seller copies.
 * Pure functions: the Worker, the pages and the unit tests share them.
 */

const HANDLE = "[a-z0-9]+(?:-[a-z0-9]+)*";
const JOIN = new RegExp(`^/@(${HANDLE})/affiliate/?$`, "i");
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const JOIN_HANDLE_MAX = 40;

export function affiliateJoinPath(handle: string): string {
  return `/@${handle}/affiliate`;
}

/** The handle in /@<handle>/affiliate, lower-cased, or null when the path is not one. */
export function parseAffiliateJoinPath(pathname: string): string | null {
  let path: string;
  try {
    path = decodeURIComponent(pathname);
  } catch {
    return null;
  }
  const m = JOIN.exec(path);
  if (!m || m[1].length > JOIN_HANDLE_MAX) return null;
  return m[1].toLowerCase();
}

export function isStoreIdHandle(handle: string): boolean {
  return UUID.test(handle);
}

/**
 * The handle a store's join page is given. Its FYP handle first, the name its
 * viewers already know from /@<handle>. Then its storefront handle, unless
 * another store's FYP profile owns that name: the page looks FYP handles up
 * first, so the link would open the other store. Its id is the last resort,
 * always unique and always resolvable.
 */
export function chooseInviteHandle(input: {
  fypSlug: string | null;
  storeSlug: string | null;
  storeSlugOwnedByOtherFyp: boolean;
  storeId: string;
}): string {
  if (input.fypSlug) return input.fypSlug;
  const s = input.storeSlug?.toLowerCase() ?? null;
  if (s && s.length <= JOIN_HANDLE_MAX && new RegExp(`^${HANDLE}$`).test(s) && !input.storeSlugOwnedByOtherFyp) return s;
  return input.storeId.toLowerCase();
}

/** A product's picture as the public pages serve it: by product, never the raw R2 key. */
export function joinProductImage(productId: string, featuredImage: string | null | undefined): string | null {
  return featuredImage ? `/api/public/products/${encodeURIComponent(productId)}/image` : null;
}

/**
 * One row per product as a visitor would tell them apart. Stores keep several
 * copies of one product (one per funnel or page), and a list of the same name
 * and price four times reads as broken. The first copy in the given order wins.
 */
export function dedupeJoinProducts<T extends { name: string; priceCents: number }>(rows: T[]): T[] {
  const seen = new Set<string>();
  return rows.filter((r) => {
    const key = `${r.name.trim().toLowerCase().replace(/\s+/g, " ")}|${r.priceCents}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export function formatRm(cents: number): string {
  const [whole, frac] = (Math.max(0, Math.round(cents)) / 100).toFixed(2).split(".");
  return `RM${whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}.${frac}`;
}

export type JoinCommission = { type: "percentage" | "flat"; percent: number; flatCents: number };

/** "20%" or "RM5.00", as the programme pays per sale. */
export function commissionLabel(c: JoinCommission): string {
  if (c.type === "flat") return formatRm(c.flatCents);
  const p = Number(c.percent) || 0;
  return `${Number.isInteger(p) ? p : p.toFixed(1)}%`;
}

/**
 * The message a seller pastes into WhatsApp with their invite link: what joining
 * pays, one real example when the store has a product that earns, and the link.
 */
export function inviteMessage(input: {
  storeName: string;
  commission: JoinCommission;
  example?: { name: string; earnCents: number } | null;
  url: string;
}): string {
  const rate = `Dapat ${commissionLabel(input.commission)} setiap jualan`;
  const example = input.example && input.example.earnCents > 0 ? `, contohnya ${formatRm(input.example.earnCents)} untuk ${input.example.name}` : "";
  return [
    "Assalamualaikum! Nak buat duit sampingan dari telefon?",
    `Jadi affiliate ${input.storeName.trim()}. ${rate}${example}.`,
    "Daftar 1 minit dengan emel, terus dapat link sendiri:",
    input.url,
  ].join("\n");
}

export function whatsappShareUrl(text: string): string {
  return `https://wa.me/?text=${encodeURIComponent(text)}`;
}

export function telegramShareUrl(url: string, text: string): string {
  return `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`;
}

export function facebookShareUrl(url: string): string {
  return `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
}
